import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Phone, MessageCircle, MapPin } from "lucide-react";
import { SlideToConfirm } from "@/components/SlideToConfirm";

interface PickupStopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stop: {
    id: string;
    sellerName: string;
    sellerPhone?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    lat?: number | string | null;
    lng?: number | string | null;
    orderId?: string | null;
  } | null;
  // The compressed photo is handed up as a blob; the parent uploads it online
  // or queues it to the offline outbox. Not uploaded here, so the driver can
  // confirm a pickup even in a dead zone at the farm.
  onConfirm: (args: { stopId: string; photoBlob: Blob }) => Promise<void> | void;
  isSubmitting: boolean;
}

async function compressForUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("image decode failed"));
    el.src = dataUrl;
  });
  const MAX = 1600;
  let w = img.width;
  let h = img.height;
  if (w > MAX || h > MAX) {
    if (w >= h) {
      h = Math.round(h * (MAX / w));
      w = MAX;
    } else {
      w = Math.round(w * (MAX / h));
      h = MAX;
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, w, h);
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.8),
  );
  if (!blob) return file;
  return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export function PickupStopDialog({
  open,
  onOpenChange,
  stop,
  onConfirm,
  isSubmitting,
}: PickupStopDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);

  function reset() {
    setPhotoBlob(null);
    setPhotoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreparing(false);
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setPhotoBlob(null);
    setPreparing(true);
    const compressed = await compressForUpload(file).catch(() => file);
    setPhotoBlob(compressed);
    setPreparing(false);
  }

  const mapsHref = (() => {
    if (!stop) return null;
    if (stop.lat != null && stop.lng != null && Math.abs(Number(stop.lat)) > 0.1) {
      return `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}&travelmode=driving`;
    }
    if (stop.address || stop.city) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        [stop.address, stop.city, stop.state].filter(Boolean).join(", "),
      )}&travelmode=driving`;
    }
    return null;
  })();

  const canSlide = Boolean(stop && photoBlob && !preparing && !isSubmitting);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm pickup from {stop?.sellerName ?? "seller"}</DialogTitle>
          <DialogDescription>
            Take a photo of the handoff so there's a record. You need to be within 150m of pickup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {stop && (
            <div className="flex flex-wrap gap-2">
              {mapsHref && (
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Open in Maps
                </a>
              )}
              {stop.sellerPhone && (
                <a
                  href={`tel:${stop.sellerPhone}`}
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Call
                </a>
              )}
              {stop.orderId && (
                <a
                  href={`/orders/${stop.orderId}`}
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  Message
                </a>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
          />

          {photoPreviewUrl ? (
            <div className="space-y-2">
              <div className="overflow-hidden rounded-lg border">
                <img src={photoPreviewUrl} alt="Pickup proof" className="block w-full object-cover" />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{preparing ? "Preparing photo…" : photoBlob ? "Photo ready" : ""}</span>
                <button
                  type="button"
                  className="text-primary underline-offset-2 hover:underline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={preparing || isSubmitting}
                >
                  Retake
                </button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="mr-2 h-4 w-4" />
              Take pickup photo
            </Button>
          )}

          <div className="pt-1">
            <SlideToConfirm
              label={canSlide ? "Slide to confirm pickup" : "Take a photo first"}
              disabled={!canSlide}
              loading={isSubmitting}
              onConfirm={async () => {
                if (!stop || !photoBlob) return;
                await onConfirm({ stopId: stop.id, photoBlob });
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
